import type { BodyHandle, World } from 'box3d-web';
import {
  fractureBoxAnisotropic,
  fractureBoxBrickCourses,
  fractureBoxNonUniform,
  fractureBoxRadialShatter,
  type FractureCell,
  type Vec3,
} from '../../services/fracture/voronoiFracture';
import { shrinkHullPoints } from '../../services/fracture/cellGeometry';
import {
  profileWallHalf,
  surfaceSpawnOptions,
  type DestructionProfile,
} from '../../services/destructionProfiles';
import { pushInvariant, resetInvariantMonitor } from '../../services/invariantMonitor';
import { addOwnedBox, addOwnedHull, createOwnedBody, destroyOwnedBody } from './nativeOwnership';
import {
  BLAST_VOLLEY_SUB_BUDGET,
  CHUNK_FRICTION,
  CHUNK_HULL_SHRINK,
  CHUNK_RESTITUTION,
  MAX_LIVE_CELLS_MUL,
  PROXY_SHRINK,
  PROXY_SHRINK_THIN,
  THIN_VOLUME_RATIO,
} from './demolitionTuning';
import type { CellRecord, DemolitionNativeParticipant, DemolitionRuntime } from './types';

export function rebuildLiveCellIndex(rt: DemolitionRuntime) {
  rt.cellById.clear();
  for (const cell of rt.cells) rt.cellById.set(cell.id, cell);
}

export function setLiveCells(rt: DemolitionRuntime, cells: FractureCell[]) {
  rt.cells = cells;
  rebuildLiveCellIndex(rt);
}

export function liveCellById(rt: DemolitionRuntime, cellId: number): FractureCell | undefined {
  return rt.cellById.get(cellId);
}

/** Build base partition cells for a load-time profile (empty for impact-timed glass). */
export function buildBasePartition(profile: DestructionProfile): FractureCell[] {
  if (profile.partitionTiming === 'impact') return [];

  const half = profileWallHalf(profile);
  const spec = profile.seedSpec;
  if (spec.kind === 'brickCourse') {
    return fractureBoxBrickCourses(half, {
      courseHeight: spec.courseHeight,
      brickLength: spec.brickLength,
      jitter: spec.jitter,
      seed: spec.seed,
    });
  }
  if (spec.kind === 'anisotropic') {
    return fractureBoxAnisotropic(half, {
      minSpacingX: spec.minSpacingX,
      maxSpacingX: spec.maxSpacingX,
      minSpacingY: spec.minSpacingY,
      maxSpacingY: spec.maxSpacingY,
      zLayers: spec.zLayers,
      jitter: spec.jitter,
      seed: spec.seed,
    });
  }
  if (spec.kind === 'nonUniform') {
    return fractureBoxNonUniform(half, {
      jitter: spec.jitter,
      minSpacing: spec.minSpacing,
      maxSpacing: spec.maxSpacing,
      zLayers: spec.zLayers,
      seed: spec.seed,
    });
  }
  // radialShatter is impact-only
  return [];
}

/** Generate impact-centered radial spiderweb for glass. centerLocal in wall space. */
export function buildImpactPartition(
  profile: DestructionProfile,
  centerLocal: Vec3,
): { cells: FractureCell[]; ms: number } {
  const t0 = performance.now();
  const half = profileWallHalf(profile);
  const spec = profile.seedSpec;
  let cells: FractureCell[] = [];
  if (spec.kind === 'radialShatter') {
    cells = fractureBoxRadialShatter(half, centerLocal, {
      rings: spec.rings,
      spokes: spec.spokes,
      jitter: spec.jitter,
      seed: spec.seed,
    });
  } else {
    // Fallback: treat as load-time partition if misconfigured.
    cells = buildBasePartition(profile);
  }
  return { cells, ms: performance.now() - t0 };
}

// ---------------------------------------------------------------------------
// Proxy helpers
// ---------------------------------------------------------------------------

export function proxyHalfForCell(cell: FractureCell, proxyShrink = PROXY_SHRINK): Vec3 {
  const ax = cell.aabb.max.x - cell.aabb.min.x;
  const ay = cell.aabb.max.y - cell.aabb.min.y;
  const az = cell.aabb.max.z - cell.aabb.min.z;
  const aabbVol = Math.max(ax * ay * az, 1e-12);
  const ratio = cell.volume / aabbVol;
  const thinShrink = Math.min(proxyShrink, PROXY_SHRINK_THIN);
  const shrink = ratio < THIN_VOLUME_RATIO ? thinShrink : proxyShrink;
  return {
    x: (ax / 2) * shrink,
    y: (ay / 2) * shrink,
    z: (az / 2) * shrink,
  };
}

export function worldCentroid(rt: DemolitionRuntime, cell: FractureCell): Vec3 {
  return {
    x: cell.centroid.x + rt.wallCenter.x,
    y: cell.centroid.y + rt.wallCenter.y,
    z: cell.centroid.z + rt.wallCenter.z,
  };
}

// ---------------------------------------------------------------------------
// Intact monolith
// ---------------------------------------------------------------------------

export function spawnMonolith(rt: DemolitionRuntime, advanceEpoch = true) {
  clearWallBodies(rt);

  // Restore from PRISTINE partition only — never from a pre-crack-polluted list.
  // Impact-timed profiles (glass) keep an empty pristine until first hit.
  rebuildAdjacencyFromPristine(rt);
  setLiveCells(rt, rt.pristineCells.slice());
  rt.pristineDamage.clear();
  let maxId = 0;
  for (const cell of rt.pristineCells) {
    if (cell.id > maxId) maxId = cell.id;
  }
  rt.nextCellId = maxId + 1;
  rt.pristineCellCount = rt.pristineCells.length;
  rt.maxLiveCells = Math.max(rt.pristineCellCount * MAX_LIVE_CELLS_MUL, MAX_LIVE_CELLS_MUL);
  rt.baseCellCount = rt.pristineCellCount;
  rt.baseCells = rt.pristineCells;

  const bodyId = createOwnedBody(rt, {
    type: 'static',
    position: [rt.wallCenter.x, rt.wallCenter.y, rt.wallCenter.z],
  }, { kind: 'monolith' });
  addOwnedBox(rt, bodyId, [rt.wallHalf.x, rt.wallHalf.y, rt.wallHalf.z], {
    density: 0,
    friction: CHUNK_FRICTION,
    restitution: CHUNK_RESTITUTION,
  }, { kind: 'monolith' });
  rt.monolithBodyId = bodyId;
  rt.staticBodyCount += 1;
  rt.fractured = false;
  rt.wallVersion += 1;
  rt.dynamicChunks = 0;
  rt.staticCells = 0;
  rt.subChunks = 0;
  rt.shardParticles = 0;
  rt.minChunkVol = 0;
  rt.maxChunkVol = 0;
  rt.lastCarveMs = 0;
  rt.lastPartitionMs = 0;
  rt.collapsedUnsupported = 0;
  rt.removedChunks = 0;
  rt.lastImpact = null;
  rt.collapseQueue = [];
  rt.remnantDirty = true;
  rt.idsDirty = true;
  // Cancel pending charges + clear markers (ChargeMarkers reads rt.charges).
  rt.charges = [];
  rt.volleySubBudget = BLAST_VOLLEY_SUB_BUDGET;
  rt.preCrackedCells = 0;
  rt.lastBlast = null;
  if (advanceEpoch) {
    rt.eventRing.reset();
    rt.damageLedger.reset();
  }
  rt.liveCellCapHits = 0;
  resetInvariantMonitor(rt.invariantMon);
}

export function rebuildAdjacencyFromPristine(rt: DemolitionRuntime) {
  rt.adjacency.clear();
  for (const cell of rt.pristineCells) {
    rt.adjacency.set(cell.id, cell.neighbors.slice());
  }
}

export function clearWallBodies(rt: DemolitionRuntime) {
  if (rt.monolithBodyId) {
    try {
      destroyOwnedBody(rt, rt.monolithBodyId as BodyHandle);
    } catch {
      // ignore
    }
    rt.monolithBodyId = 0;
  }
  for (const rec of rt.records.values()) {
    if (rec.bodyId) {
      try {
        destroyOwnedBody(rt, rec.bodyId as BodyHandle);
      } catch {
        // ignore
      }
    }
  }
  rt.records.clear();
  rt.cannonballPool.destroyAll();
  rt.cannonballs.length = 0;
  rt.cannonballSet.clear();
  rt.projectileWeapons.clear();
  rt.projectilePowers.clear();
  rt.projectileRecoveryUntil.clear();
  rt.projectileRestDwell.clear();
  rt.projectileRecoveryCount.clear();
  rt.projectileOrdinals.clear();
  rt.embeddedProjectiles.clear();
  rt.projectileGroundTraced.clear();
  // staticBodyCount is ground only after this (1)
  rt.staticBodyCount = 1;
  rt.idsDirty = true;
}

/**
 * Dispose only the structural participants of a runtime. A hosted runtime's
 * shared World and supplied ground remain usable; repeated calls are no-ops.
 */
export function disposeDemolitionStructure(rt: DemolitionRuntime): void {
  if (rt.structureDisposed === true) return;
  let firstError: unknown;
  try {
    clearWallBodies(rt);
  } catch (error) {
    firstError = error;
  }
  if (rt.ownsGround && rt.groundBodyId) {
    try {
      destroyOwnedBody(rt, rt.groundBodyId as BodyHandle);
    } catch (error) {
      if (firstError === undefined) firstError = error;
    } finally {
      rt.groundBodyId = 0;
    }
  }
  rt.structureDisposed = true;
  if (firstError !== undefined) throw firstError;
}

/** Commit an impact-timed partition as latent damage discretization (no topology swap). */
export function commitLatentPartition(
  rt: DemolitionRuntime,
  cells: FractureCell[],
  partitionMs: number,
) {
  rt.lastPartitionMs = partitionMs;
  rt.pristineCells = cells;
  rt.pristineCellById.clear();
  for (const cell of cells) rt.pristineCellById.set(cell.id, cell);
  rt.baseCells = cells;
  rt.pristineCellCount = cells.length;
  rt.maxLiveCells = Math.max(cells.length * MAX_LIVE_CELLS_MUL, MAX_LIVE_CELLS_MUL);
  rt.baseCellCount = cells.length;
  rt.adjacency.clear();
  for (const cell of cells) {
    rt.adjacency.set(cell.id, cell.neighbors.slice());
  }
  let maxId = 0;
  for (const cell of cells) {
    if (cell.id > maxId) maxId = cell.id;
  }
  rt.nextCellId = maxId + 1;
  // Keep live registry aligned for bookkeeping; monolith body still owns physics.
  setLiveCells(rt, cells.slice());
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      `[demolition] latent impact partition ${partitionMs.toFixed(2)}ms ` +
        `cells=${cells.length} material=${rt.profile.id} (monolith kept)`,
    );
  }
}

/** Live cells currently in the working registry (bodies + pending partition). */
export function liveCellCount(rt: DemolitionRuntime): number {
  return rt.cells.length;
}

/** True if adding `extra` cells would exceed maxLiveCells. */
export function wouldExceedLiveCap(rt: DemolitionRuntime, extra: number): boolean {
  return liveCellCount(rt) + extra > rt.maxLiveCells;
}

export function noteLiveCapHit(rt: DemolitionRuntime) {
  rt.liveCellCapHits += 1;
  if (import.meta.env.DEV) {
    pushInvariant(
      rt.invariantMon,
      'max-live-cells',
      liveCellCount(rt),
      rt.stepper.simTime,
    );
  }
}

export function applyShapeSurface(
  rt: DemolitionRuntime,
  shapeId: number,
  friction: number,
  restitution: number,
) {
  const world = rt.world as World & {
    setShapeFriction: (shape: number, f: number) => void;
    setShapeRestitution: (shape: number, r: number) => void;
  };
  world.setShapeFriction(shapeId, friction);
  world.setShapeRestitution(shapeId, restitution);
}

export function spawnCellBody(
  rt: DemolitionRuntime,
  cell: FractureCell,
  type: 'static' | 'dynamic',
  opts: { isSubChunk?: boolean; preCrackGen?: number; damage?: number } = {},
): CellRecord {
  const wc = worldCentroid(rt, cell);
  // proxyShrink is the box path's clearance mechanism (baked into half);
  // CHUNK_HULL_SHRINK is the hull path's. They must not be combined.
  const half = proxyHalfForCell(cell, rt.profile.proxyShrink);
  const density = type === 'dynamic' ? rt.profile.density : 0;
  const mass = rt.profile.density * cell.volume;
  const surface = surfaceSpawnOptions(rt.profile);
  const friction = type === 'dynamic' ? surface.friction : CHUNK_FRICTION;
  const restitution = type === 'dynamic' ? surface.restitution : CHUNK_RESTITUTION;
  const linearDamping = type === 'dynamic' ? surface.linearDamping : 0;
  const angularDamping = type === 'dynamic' ? surface.angularDamping : 0;

  const participant: DemolitionNativeParticipant = { kind: 'cell', cellId: cell.id };
  const bodyId = createOwnedBody(rt, {
    type,
    position: [wc.x, wc.y, wc.z],
    ccd: type === 'dynamic',
    linearDamping,
    angularDamping,
  }, participant);
  // Hull path: shrink geometry by CHUNK_HULL_SHRINK; scale density by 1/f³ so
  // engine mass still equals profile.density × cell.volume.
  const hullShrink = type === 'dynamic' ? CHUNK_HULL_SHRINK : 1;
  const colliderDensity =
    type === 'dynamic' ? density / (CHUNK_HULL_SHRINK ** 3) : density;
  const shapeId = addOwnedHull(
    rt,
    bodyId,
    shrinkHullPoints(cell.vertices, cell.centroid, hullShrink),
    { density: colliderDensity, friction, restitution },
    participant,
  ) as number;
  if (type === 'dynamic') {
    applyShapeSurface(rt, shapeId as number, friction, restitution);
    // Prefer explicit body damping setters when present (mirrors surface record).
    const w = rt.world as World & {
      setLinearDamping?: (body: BodyHandle, d: number) => void;
      setAngularDamping?: (body: BodyHandle, d: number) => void;
    };
    try {
      w.setLinearDamping?.(bodyId as BodyHandle, linearDamping);
      w.setAngularDamping?.(bodyId as BodyHandle, angularDamping);
    } catch {
      // ignore
    }
  }

  if (type === 'static') rt.staticBodyCount += 1;

  const order = type === 'dynamic' ? rt.dynamicOrderCounter++ : -1;
  const rec: CellRecord = {
    cellId: cell.id,
    bodyId,
    state: type,
    bufferIndex: -1,
    dynamicOrder: order,
    worldCentroid: wc,
    halfProxy: half,
    volume: cell.volume,
    mass,
    fade: -1,
    isSubChunk: opts.isSubChunk === true,
    preCrackGen: opts.preCrackGen ?? (opts.isSubChunk ? 1 : 0),
    damage: Math.max(0, opts.damage ?? 0),
  };
  rt.records.set(cell.id, rec);
  return rec;
}

/**
 * Remove a cell id from the LIVE registry (rt.cells + adjacency).
 * Never touches pristineCells (F1).
 */
export function removeCellFromLiveRegistry(rt: DemolitionRuntime, cellId: number) {
  setLiveCells(rt, rt.cells.filter((c) => c.id !== cellId));
  rt.adjacency.delete(cellId);
  for (const [id, nbs] of rt.adjacency) {
    if (nbs.includes(cellId)) {
      rt.adjacency.set(
        id,
        nbs.filter((n) => n !== cellId),
      );
    }
  }
}
